import { disposeMesh } from 'src/utils/object3DHelpers';
import { AnimationMixer, Line, Mesh, Object3D } from 'three';
import { SceneComponent } from '../SceneComponent';
import { Inputs } from './types';
import { createCircleIconSVG } from 'src/utils/creatCircleIconSvg';
import { svgToPng } from 'src/utils/svgToPng';

export class TagEditObject extends SceneComponent {
  private root: Object3D;

  private pivot: Object3D;

  private circle: Mesh | null = null;

  private line: Line | null = null;

  private mixer: AnimationMixer;

  private stemVec3;

  private cameraPos;

  private circleWorldPos;

  private textureLoader;

  inputs: Inputs = {
    stemVector: null,
    normal: { x: 0, y: 0.3, z: 0 },
    pose: null,
    tag: null,
  };

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    this.outputs.objectRoot = this.root;
    this.root?.add(this.pivot);
    this.stemVec3 = new THREE.Vector3(0, 0, 0);
    this.cameraPos = new THREE.Vector3(0, 0, 0);
    this.circleWorldPos = new THREE.Vector3(0, 0, 0);
    this.textureLoader = new THREE.TextureLoader();

    this.makeTag();
  }

  onDestroy() {
    if (this.circle) {
      disposeMesh(this.circle);
      this.circle = null;
    }
    if (this.line) {
      disposeMesh(this.line);
      this.line = null;
    }
  }

  onInputsUpdated() {}

  private async makeTag() {
    const THREE = this.context.three;

    if (this.circle) {
      disposeMesh(this.circle);
      this.circle = null;
    }
    if (this.line) {
      disposeMesh(this.line);
      this.line = null;
    }

    let { stemVector } = this.inputs;
    if (!stemVector) {
      stemVector = new THREE.Vector3(0, 0.3, 0);
    }
    if (stemVector) {
      // draw a circle
      const tag = this.inputs.tag;
      let iconSVG;
      if (this.inputs.tag?.iconColor) iconSVG = createCircleIconSVG(this.inputs.tag.iconColor);
      else iconSVG = createCircleIconSVG('#2055ff');

      if (tag && !tag.isNew) return;

      const iconSrc = svgToPng(iconSVG);

      this.textureLoader.load(iconSrc, (texture) => {
        const aspect = texture.image.width / texture.image.height;
        //@ts-ignore will be required for custom icons
        const [width, height] = aspect >= 1 ? [1, 1 / aspect] : [1 / aspect, 1];
        const geometry = new THREE.CircleGeometry(0.035, 36);

        // const geometry = new THREE.PlaneGeometry(width, height);

        const material = new THREE.MeshBasicMaterial({
          map: texture,
          side: THREE.DoubleSide,
          blending: THREE.NormalBlending,
          transparent: true,
        });
        this.circle = new THREE.Mesh(geometry, material);
        //@ts-ignore
        this.circle.position.set(stemVector.x, stemVector.y, stemVector.z);
        if (this.circle) this.root?.add(this.circle);
      });

      // draw a line
      const points: Array<THREE.Vector3> = [];
      points.push(new THREE.Vector3(0, 0, 0));
      points.push(new THREE.Vector3(stemVector.x, stemVector.y, stemVector.z));
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const lineMaterial = new THREE.LineBasicMaterial({ color: 0xffffff });
      this.line = new THREE.Line(lineGeometry, lineMaterial);
      if (this.line) this.root?.add(this.line);
    }
  }

  onTick(delta: number) {
    const THREE = this.context.three;
    const points: Array<THREE.Vector3> = [];
    if (this.line && this.inputs.stemVector) {
    }
    if (this.line) {
      if (this.inputs.tag)
        this.stemVec3.set(
          this.inputs.tag.stemVectorX,
          this.inputs.tag.stemVectorY,
          this.inputs.tag.stemVectorZ,
        );
      else {
        this.stemVec3.copy(this.inputs.normal).clampLength(0, 0.3);
      }

      const { x, y, z } = this.stemVec3;
      this.circle?.position.set(x, y, z);
      points.push(new THREE.Vector3(0, 0, 0));
      points.push(this.stemVec3);
      this.line.geometry.setFromPoints(points);
    }

    if (this.mixer) {
      this.mixer.update(delta / 1000);
    }
    if (this.circle) {
      if (this.inputs.pose) {
        const { x, y, z } = this.inputs.pose.position;
        this.cameraPos.set(x, y, z);

        this.circle.lookAt(this.cameraPos);
        this.circle.getWorldPosition(this.circleWorldPos);
        const distanceScale = this.cameraPos.distanceTo(this.circleWorldPos);
        this.circle.scale.setScalar(Math.pow(distanceScale, 0.5));
      }
    }
  }
}

export const tagEditObjectType = 'treedis.tagEditObject';
export const makeTagEditObjectComponent = function (): any {
  return new TagEditObject();
};
